import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatCategory } from '../../main-shared/categories-display/flat-category';
import { Category } from '../../main-shared/categories-display/category';
import { Image } from '../../shared/image-upload/image';

export function MinWordCountValidator(minCount: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    let words: string[] = control.value.split(' ');
    words = words.filter((word) => word); // remove empty strings
    const wordCount = words.length;
    return wordCount >= minCount
      ? null
      : { minWordCount: { value: control.value } };
  };
}

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProductFormComponent),
      multi: true,
    },
  ],
})
export class ProductFormComponent
  implements ControlValueAccessor, Validator, OnChanges, OnInit
{
  @Input() rootCategories: Category[] = [];
  @Input() fileNames: string[] = [];
  productForm = this.formBuilder.group({
    name: ['', Validators.required],
    description: ['', [Validators.required, MinWordCountValidator(5)]],
    free: this.formBuilder.nonNullable.control(false),
    price: [
      2.0,
      Validators.compose([Validators.min(2.0), Validators.required]),
    ],
    imagesInput: new FormControl<Image[] | string>([], {
      nonNullable: true,
    }),
    filesInput: new FormControl<File[]>([], {
      nonNullable: true,
    }),
  });
  treeControl: FlatTreeControl<FlatCategory>;
  treeFlattener: MatTreeFlattener<Category, FlatCategory>;
  dataSource: MatTreeFlatDataSource<Category, FlatCategory>;
  checklistSelection = new SelectionModel<FlatCategory>(true);

  constructor(private formBuilder: FormBuilder) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<FlatCategory>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );
  }

  get priceField(): FormControl<number | null> {
    return this.productForm.controls.price;
  }

  transformer = (node: Category, level: number) => {
    const flatCat: FlatCategory = {
      id: node.id,
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
    };
    return flatCat;
  };

  getLevel = (node: FlatCategory) => node.level;

  isExpandable = (node: FlatCategory) => node.expandable;

  getChildren = (node: Category) => node.children;

  ngOnInit(): void {
    this.productForm.controls.free.valueChanges.subscribe((free: boolean) => {
      if (free) {
        this.priceField.reset();
        this.priceField.disable();
      } else {
        this.priceField.enable();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataSource.data = this.rootCategories;
  }

  hasChild = (_: number, node: FlatCategory) => node.expandable;

  categoryLeafItemSelectionToggle(node: FlatCategory): void {
    this.checklistSelection.toggle(node);
  }

  getSelectedCategories(): string[] {
    return this.checklistSelection.selected.map((selected) => selected.id!);
  }

  setSelectedCategories(selected: string[]): void {
    const allNodes = this.treeControl.dataNodes;
    allNodes.forEach((node) => {
      if (selected.includes(node.id!)) {
        this.checklistSelection.toggle(node);
      }
    });
  }

  onTouched: () => void = () => {};

  registerOnChange(fn: any): void {
    this.productForm.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    if (value) {
      this.productForm.setValue(value, { emitEvent: false });
      if (value?.free) {
        this.priceField.disable();
      }
    } else {
      this.productForm.reset();
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.productForm.disable();
    } else {
      this.productForm.enable();
    }
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.productForm.valid
      ? null
      : {
          invalidForm: {
            valid: false,
            message: 'productForm fields are invalid',
          },
        };
  }
}
